import { Auth, API } from 'aws-amplify';

/**
 * AuthAPI contains all static methods to check the user identity as logged user,
 * check if is admin, make the common actions as an user (such as login, confirm 
 * account, recover passwords or logout) and allow to current user to change its
 * information.
 * 
 * It uses the Auth SDK of AWS Amplify framework.
 *
 * @class AuthAPI
 */
export default class AuthAPI {
    static async imAdmin() {
        try {
            const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
            const groups = user.signInUserSession.accessToken.payload["cognito:groups"];
            return [ true, groups.includes("Admins") ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async authenticatedUser() {
        try {
            const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
            const tokenPayload = user.signInUserSession.accessToken.payload;
            user.isAdmin = Object.prototype.hasOwnProperty.call(tokenPayload, 'cognito:groups') &&
                tokenPayload['cognito:groups'].includes("Admins");
            try {
                user.app_version = JSON.parse(user.attributes['custom:app_version']);
            } catch {
                user.app_version = [];
            }

            try {
                user.attributes.clinics = JSON.parse(user.attributes['custom:clinics']);
            } catch {
                user.attributes.clinics = [];
            }

            return [ true, user ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async anonymousUser(token) {
        try {
            const user = await Auth.currentCredentials();
            const chat_name = await API.get('chatsapi', '/find-chat-name', {
                queryStringParameters: { ghl_key: token }
            });
            if (chat_name.clinic){
                user.attributes = {
                    clinics: [chat_name.clinic],
                    email: `${token}@ghl_user`,
                    email_verified: true,
                    name: "Anonymous"
                }
                return [ true, user ];
            }
            else return [ false ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async anonymousUserByAccountId(accountId) {
        try {
            const user = await Auth.currentCredentials();
            const response = await API.get('chatsapi', '/find-chat-name-by-account', {
                queryStringParameters: { account_id: accountId }
            });
            if (response.clinic){
                user.attributes = {
                    clinics: [response.clinic],
                    email: `${accountId}@account_user`,
                    email_verified: true,
                    name: "Anonymous"
                }
                return [ true, user ];
            }
            else return [ false ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async currentUser() {
        try {
            let [ success, user ] = await AuthAPI.authenticatedUser();
            if (!success) {
                const queryString = window.location.search;
                const params = new URLSearchParams(queryString);
                const accountId = params.get("account_id");
                const token = params.get("ghl_key");

                // Prefer account_id if present, fallback to ghl_key
                if (accountId) {
                    [ success, user ] = await AuthAPI.anonymousUserByAccountId(accountId);
                } else if (token) {
                    [ success, user ] = await AuthAPI.anonymousUser(token);
                }
            }

            return [ success, user ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async login(email, password) {
        try {
            const user = await Auth.signIn(email, password);
            return [ true, user ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async newPassword(email, newPassword) {
        try {
            const user = await Auth.completeNewPassword(email, newPassword);
            return [ true, user ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async sendRecoveryPasswordCode(email) {
        try {
            const result = await Auth.forgotPassword(email);
            return [ true, result ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async recoveryPassword(email, code, newPassword) {
        try {
            const result = await Auth.forgotPasswordSubmit(email, code, newPassword);
            return [ true, result ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async logOut() {
        try {
            await Auth.signOut({ global: true });
            return [ true, {} ];
        } catch (error) {
            return [ false, error ];
        }
    }

    static async confirmAccount(email, code) {
        try {
            await Auth.confirmSignUp(email, code);
            return [ true, null ];
        } catch (error) {
            return [ false, error.message ];
        }
    }

    static async changeName(name) {
        try {
            const user = await Auth.currentAuthenticatedUser();
            const result = await Auth.updateUserAttributes(user, { name });
            return [ true, result ];
        } catch (error) {
            return [ false, error.message ];
        }
    }

    static async changePassword(oldPassword, newPassword) {
        try {
            const user = await Auth.currentAuthenticatedUser();
            const result = await Auth.changePassword(user, oldPassword, newPassword);
            return [ true, result ];
        } catch (error) {
            return [ false, error.message ];
        }
    }
}